/**
 * Transformer for ```mermaid fenced code blocks.
 *
 * Lexical's built-in CODE transformer would otherwise turn a ```mermaid fence
 * into a plain CodeNode (rendered as monospace text). This transformer is
 * registered before Lexical's CODE transformer in LOAD_TRANSFORMERS so it
 * intercepts mermaid fences first and converts them into a MermaidNode, which
 * renders the diagram.
 *
 * `regExpStart` only matches fences whose info string is exactly "mermaid", so
 * ordinary code blocks (```js, ```diff, etc.) are left untouched.
 *
 * `handleImportAfterStartMatch` takes over after the start match and manually
 * scans for the closing fence (same backtick count as the opener), then creates
 * a MermaidNode directly. Export round-trips back to a standard 3-backtick
 * ```mermaid fence so the markdown stored on the ClinicalImpression is stable.
 */

import type { MultilineElementTransformer } from '@lexical/markdown';
import {
  $createMermaidNode,
  $isMermaidNode,
  MermaidNode,
} from '../customNodes/MermaidNode/MermaidNode';

/**
 * Matches an opening fence of 3 or more backticks whose info string is exactly
 * "mermaid". Leading whitespace is allowed, mirroring Lexical's own
 * CODE_START_REGEX which also permits indented fences.
 */
const MERMAID_FENCE_START = /^[ \t]*(`{3,})mermaid[ \t]*$/;

export const MERMAID: MultilineElementTransformer = {
  get dependencies() {
    return [MermaidNode];
  },

  regExpStart: MERMAID_FENCE_START,

  handleImportAfterStartMatch: ({
    lines,
    rootNode,
    startLineIndex,
    startMatch,
  }) => {
    const fenceLen = startMatch[1].length;
    const closingFenceRegex = new RegExp(`^[ \\t]*\`{${fenceLen}}[ \\t]*$`);

    const bodyLines: string[] = [];
    let endLineIndex = startLineIndex + 1;

    while (endLineIndex < lines.length) {
      if (closingFenceRegex.test(lines[endLineIndex])) {
        break;
      }
      bodyLines.push(lines[endLineIndex]);
      endLineIndex++;
    }

    // If we reached EOF without finding the closing fence, bail out and let
    // other transformers handle the line. Malformed / truncated markdown should
    // not silently absorb the rest of the document into a diagram.
    if (endLineIndex >= lines.length) {
      return null;
    }

    const chart = bodyLines.join('\n');
    const mermaidNode = $createMermaidNode(chart);
    rootNode.append(mermaidNode);

    return [true, endLineIndex];
  },

  replace: () => {
    // Not called: handleImportAfterStartMatch handles all import cases.
  },

  export: node => {
    if (!$isMermaidNode(node)) {
      return null;
    }
    // Export always uses 3-backtick format (standard round-trip).
    return `\`\`\`mermaid\n${node.getChart()}\n\`\`\``;
  },

  type: 'multiline-element',
};
